import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

/**
 * SUPER_ADMIN hangar onboarding: create Operator API keys + set webhook URL.
 */
const HangarOperatorKeysPanel = () => {
  const { showToast } = useToast();
  const [operators, setOperators] = useState([]);
  const [operatorId, setOperatorId] = useState('');
  const [keys, setKeys] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [lastCreatedKey, setLastCreatedKey] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/master-data/operators', { params: { isActive: 'true' } })
      .then((res) => setOperators(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOperators([]));
  }, []);

  const loadKeys = useCallback(async (id) => {
    if (!id) {
      setKeys([]);
      return;
    }
    try {
      const res = await axios.get(`/api/mro/operators/${id}/api-keys`);
      setKeys(Array.isArray(res.data) ? res.data : []);
      const op = operators.find((o) => String(o._id) === String(id));
      setWebhookUrl(op?.webhookUrl || '');
    } catch (err) {
      setKeys([]);
      showToast(err.response?.data?.message || 'Failed to load API keys', 'error');
    }
  }, [operators, showToast]);

  useEffect(() => {
    if (operatorId) loadKeys(operatorId);
  }, [operatorId, loadKeys]);

  const createKey = async () => {
    if (!operatorId) return;
    setLoading(true);
    try {
      const res = await axios.post(`/api/mro/operators/${operatorId}/api-keys`, { name: 'Hangar ingest' });
      setLastCreatedKey(res.data.apiKey || '');
      showToast('API key created — copy it now', 'success');
      await loadKeys(operatorId);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create key', 'error');
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (keyId) => {
    try {
      await axios.delete(`/api/mro/operators/${operatorId}/api-keys/${keyId}`);
      showToast('API key revoked', 'success');
      await loadKeys(operatorId);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to revoke', 'error');
    }
  };

  const saveWebhook = async () => {
    if (!operatorId) return;
    try {
      await axios.put(`/api/mro/operators/${operatorId}/webhooks`, {
        webhookUrl,
        rotateSecret: !operators.find((o) => String(o._id) === String(operatorId))?.webhookSecret
      });
      showToast('Webhook saved', 'success');
      const opRes = await axios.get('/api/master-data/operators', { params: { isActive: 'true' } });
      setOperators(Array.isArray(opRes.data) ? opRes.data : []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save webhook', 'error');
    }
  };

  return (
    <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle1" gutterBottom>Operator API keys &amp; webhooks</Typography>
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <TextField
          select
          label="Operator"
          value={operatorId}
          onChange={(e) => {
            setOperatorId(e.target.value);
            setLastCreatedKey('');
          }}
          fullWidth
        >
          {operators.map((op) => (
            <MenuItem key={op._id} value={op._id}>{op.code} — {op.name}</MenuItem>
          ))}
        </TextField>
        {operatorId && (
          <>
            <Button variant="outlined" onClick={createKey} disabled={loading}>Create API key</Button>
            {lastCreatedKey && (
              <Alert severity="warning">
                New key (shown once): <code style={{ wordBreak: 'break-all' }}>{lastCreatedKey}</code>
              </Alert>
            )}
            {keys.filter((k) => k.isActive).map((k) => (
              <Stack key={k._id} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{k.keyPrefix}… ({k.name})</Typography>
                <Button size="small" color="error" onClick={() => revokeKey(k._id)}>Revoke</Button>
              </Stack>
            ))}
            <TextField
              label="Webhook URL"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              fullWidth
              placeholder="https://outside-camo.example/hooks/hangar"
            />
            <Button variant="contained" onClick={saveWebhook}>Save webhook</Button>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default HangarOperatorKeysPanel;
